import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Contractor, ContractorDetails } from 'src/interfaces/contractor'
import { createAsyncAction } from 'src/utils/reduxUtils'
import {
  FetchContractor,
  FetchContractors,
  FetchContractorsCounts,
  FetchSubcontractor,
} from 'src/constants/actionTypes'
import {
  contractorPageSizeOptions,
  contractorInitialSortFields,
  contractorColumns,
} from 'src/constants/contractor/contractorGrid'
import { IContractorsTableContext, ContractorsTableContext } from './ContractorsTable.context'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'
import { getSelectedMarket } from 'src/selectors/market'
import { ContractorsHeader } from '../grid/ContractorsHeader'
import {
  ContractorState,
  ContractorStatus,
  SubcontractorHeaderTypes,
} from 'src/constants/contractor/enums'
import { ContractorDetailsModal } from 'src/components/contractor-details/ContractorDetailsModal'
import { handleApiError } from 'src/utils/errorHandlers'
import { Pagination } from 'src/interfaces/table'
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro'
import { ContractorsContext } from '../contractors/Contractors.context'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { EmptyContractors } from '../grid/EmptyContractors'

interface FetchContractorsParams {
  limit?: number
  offset?: number
  searchQuery: string
  tagsFilter: Array<string>
  statusFilter: Array<ContractorStatus>
  sortModel: GridSortModel
  selectedState: ContractorState
  selectedSubcontractorId: string
}

interface ContractorsTableProps {
  selectedState: ContractorState
  selectedSubcontractorId: string
}

const getRowId = (row: Contractor) => row.id

const ContractorsTableComponent = ({
  selectedState,
  selectedSubcontractorId,
}: ContractorsTableProps) => {
  const dispatch = useDispatch()

  const selectedMarket = useSelector(getSelectedMarket)

  const { searchQuery } = useContext(ContractorsContext)

  const { paginationModel, limit, offset, setPaginationModel } =
    useGridPagination(contractorPageSizeOptions)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [contractors, setContractors] = useState<Array<Contractor>>([])
  const [totalContractors, setTotalContractors] = useState<number>(0)
  const [statusFilter, setStatusFilter] = useState<Array<ContractorStatus>>([])
  const [tagsFilter, setTagsFilter] = useState<Array<string>>([])
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null)
  const [contractorDetails, setContractorDetails] = useState<ContractorDetails | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>(
    contractorInitialSortFields[selectedState]
      ? [
          {
            field: contractorInitialSortFields[selectedState] || 'invitedAt',
            sort: 'desc',
          },
        ]
      : [],
  )

  const fetchContractors = useCallback(
    async ({
      limit,
      offset,
      searchQuery,
      sortModel,
      selectedState,
      tagsFilter,
      statusFilter,
      selectedSubcontractorId,
    }: FetchContractorsParams) => {
      setIsLoading(true)

      try {
        const params: any = {
          limit,
          offset,
          contractorState: selectedState,
          contractorStatus: statusFilter,
        }

        if (selectedSubcontractorId === SubcontractorHeaderTypes.SUBCONTRACTOR) {
          params.onlySubcontractors = true
        }

        if (selectedSubcontractorId === SubcontractorHeaderTypes.OWN) {
          params.masterContractorRelationId = 'null'
        }

        if (
          selectedSubcontractorId !== SubcontractorHeaderTypes.ALL &&
          selectedSubcontractorId !== SubcontractorHeaderTypes.OWN &&
          selectedSubcontractorId !== SubcontractorHeaderTypes.SUBCONTRACTOR
        ) {
          params.masterContractorRelationId = selectedSubcontractorId
        }

        if (searchQuery) {
          params.contractorSearch = searchQuery
        }

        if (selectedMarket?.id) {
          params.partnerMarketId = selectedMarket?.id
        }

        if (tagsFilter?.length) {
          params.tags = tagsFilter
        }

        if (sortModel && sortModel[0]) {
          params.sort = `${sortModel[0]?.field}:${sortModel[0]?.sort?.toUpperCase()}`
        }

        const payload: { data: Array<Contractor>; pagination: Pagination } =
          await createAsyncAction(dispatch, FetchContractors.request(params))

        setContractors(payload.data)
        setTotalContractors(payload.pagination.total)
      } catch (err: unknown) {
        handleApiError(err)
      }

      setIsLoading(false)
    },
    [selectedMarket?.id],
  )

  const fetchContractorsCounts = useCallback(() => {
    const params: any = {}

    if (selectedMarket?.id) {
      params.partnerMarketId = selectedMarket?.id
    }

    if (selectedSubcontractorId === SubcontractorHeaderTypes.SUBCONTRACTOR) {
      params.onlySubcontractors = true
    }

    if (selectedSubcontractorId === SubcontractorHeaderTypes.OWN) {
      params.masterContractorRelationId = 'null'
    }

    if (
      selectedSubcontractorId !== SubcontractorHeaderTypes.ALL &&
      selectedSubcontractorId !== SubcontractorHeaderTypes.OWN &&
      selectedSubcontractorId !== SubcontractorHeaderTypes.SUBCONTRACTOR
    ) {
      params.masterContractorRelationId = selectedSubcontractorId
    }

    dispatch(FetchContractorsCounts.request(params))
  }, [selectedMarket?.id, selectedSubcontractorId])

  const refreshContractors = useCallback(async () => {
    await fetchContractors({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      statusFilter,
      selectedState,
      selectedSubcontractorId,
    })
    fetchContractorsCounts()
  }, [
    limit,
    offset,
    searchQuery,
    sortModel,
    tagsFilter,
    statusFilter,
    selectedState,
    fetchContractors,
    fetchContractorsCounts,
    selectedSubcontractorId,
  ])

  useEffect(() => {
    fetchContractors({
      limit,
      offset,
      searchQuery,
      sortModel,
      tagsFilter,
      statusFilter,
      selectedState,
      selectedSubcontractorId,
    })
    fetchContractorsCounts()
  }, [
    limit,
    offset,
    searchQuery,
    sortModel,
    tagsFilter,
    statusFilter,
    selectedState,
    selectedMarket?.id,
    selectedSubcontractorId,
  ])

  const handleRowClick = useCallback(({ row }: { row: Contractor }) => {
    setSelectedContractor(row)
  }, [])

  const handleCloseDetails = useCallback(() => {
    setSelectedContractor(null)
    setContractorDetails(null)
  }, [])

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel)
  }

  const handleChangePagination = useCallback((paginationModel: GridPaginationModel) => {
    setPaginationModel(paginationModel)
  }, [])

  const contractorsContextValue = useMemo<IContractorsTableContext>(() => {
    return {
      statusFilter,
      setStatusFilter,

      tagsFilter,
      setTagsFilter,

      refreshContractors,
    }
  }, [statusFilter, setStatusFilter, tagsFilter, setTagsFilter, refreshContractors])

  const fetchContractorDetails = useCallback(
    async (contractor: Contractor) => {
      try {
        const contractorDetails: ContractorDetails = contractor.masterContractorRelationId
          ? await createAsyncAction(
              dispatch,
              FetchSubcontractor.request({
                partnerDriverId: contractor.id,
                contractorState: selectedState,
              }),
            )
          : await createAsyncAction(
              dispatch,
              FetchContractor.request({
                contractorId: contractor.id,
                contractorState: selectedState,
              }),
            )
        setContractorDetails(contractorDetails)
      } catch (err) {
        handleApiError(err)
      }
    },
    [selectedState],
  )

  useEffect(() => {
    if (!selectedContractor) {
      setContractorDetails(null)
    } else {
      fetchContractorDetails(selectedContractor)
    }
  }, [selectedContractor?.id])

  const refreshContractorDetails = useCallback(async () => {
    if (selectedContractor) {
      await fetchContractorDetails(selectedContractor)
    }
    refreshContractors()
  }, [selectedContractor?.id, refreshContractors])

  const isSubcontractor = useMemo(() => {
    return Boolean(selectedContractor?.masterContractorRelationId)
  }, [selectedContractor?.masterContractorRelationId])

  const columns = useMemo(() => contractorColumns[selectedState], [selectedState])

  return (
    <ContractorsTableContext.Provider value={contractorsContextValue}>
      <ContractorsHeader selectedContractorState={selectedState} />
      <GridTable<Contractor>
        rows={contractors}
        isLoading={isLoading}
        columns={columns}
        paginationModel={paginationModel}
        pageSizeOptions={contractorPageSizeOptions}
        rowCount={totalContractors}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPaginationModelChange={handleChangePagination}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        EmptyElement={EmptyContractors}
      />
      {contractorDetails && (
        <ContractorDetailsModal
          key={contractorDetails.id}
          contractorDetails={contractorDetails}
          showWaitingForApprovalStepsFirst={selectedState === ContractorState.Recollection}
          flow="contractors"
          onClose={handleCloseDetails}
          onRefresh={refreshContractorDetails}
          isSubcontractor={isSubcontractor}
        />
      )}
    </ContractorsTableContext.Provider>
  )
}

export const ContractorsTable = memo(ContractorsTableComponent)
